<template>
  <div class="b24">
    <div class="site-width">
      <div class="section order-confirmation">
        <div class="order-confirmation__content">
          <div class="row">
            <div class="columns">
              <h1>
                Thank You For Your Order
              </h1>
            </div>
          </div>
          <div class="row order-confirmation__top d-flex">
            <div class="columns order-confirmation__copy">
              <p>
                Your order number is #{{ order.number }}.
                <br />
                You will receive a confirmation email shortly at
                {{ $auth.user.email }}
              </p>
              <div class="d-flex">
                <nuxt-link
                  :to="`/accounts/orders/${order.number}/`"
                  @click.native="onClickOut('post_purchase_view_order_details')"
                >
                  View order details
                </nuxt-link>
              </div>
            </div>
            <div class="columns order-confirmation__mobileapp">
              <div class="callout-mobileapp">
                <p>
                  <strong>Get delivery alerts in this mobile app.</strong>
                </p>
                <div class="callout-mobileapp__buttons">
                  <a
                    href="https://apps.apple.com/us/app/wine-access/id1642804318"
                    target="_blank"
                  >
                    <img v-lazy-load :data-src="require('~/assets/images/mobile-apple.svg')" />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.wineaccess&hl=en"
                    target="_blank"
                  >
                    <img v-lazy-load :data-src="require('~/assets/images/mobile-google.png')" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="row feature-blocks">
            <div
              v-if="order.show_add_club_get_10_pct_banner"
              class="columns feature-block feature-block--red mobile-order-1 medium-text-left w-full"
            >
              <h2>Join a Wine Club, Get 10% Off All Your Orders</h2>
              <nuxt-link
                to="/club/"
                class="link-overlay"
                @click.native="onClickOut('join_club_10_pct_clicked')"
              >
                <span class="sr-only">Find Your Perfect Club</span>
              </nuxt-link>
              <div class="row">
                <div class="columns large-5 medium-6">
                  <p class="large">
                    Get 10% credit back on this order when you join in the next
                    24 hours.
                  </p>
                  <p class="feature-block__cta">
                    <span class="underline nowrap longarrow-white"
                      >Find Your Perfect Club</span
                    >
                  </p>
                </div>
                <div class="columns large-7 medium-6 medium-text-right">
                  <CountDown />
                </div>
              </div>
            </div>
            <div class="columns feature-block mobile-order-3">
              <h2>Get $50</h2>
              <p>
                Refer a friend. When they place their first order of $150 or
                more, you’ll both get a $50 credit.
              </p>
              <p class="feature-block__cta">
                <nuxt-link
                  to="/invite/"
                  class="underline"
                  @click.native="onClickOut('post_purchase_refer_a_friend')"
                >
                  Rack Up $50 Credits
                </nuxt-link>
              </p>
            </div>
            <div
              v-if="showPPTOWidget"
              class="columns feature-block feature-block--bottlesummary mobile-order-2"
            >
              <h2>Free Shipping</h2>
              <p>Fill up your case, shipping&apos;s already free.</p>
              <p class="feature-block__cta">
                <nuxt-link
                  to="/store/"
                  class="underline"
                  @click.native="onClickOut('post_purchase_continue_shopping')"
                >
                  Continue Shopping
                </nuxt-link>
              </p>
              <div class="bottle-summary">
                <span class="sr-only"
                  >{{ 12 - order.ppto_slots_available }} slot{{
                    12 - order.ppto_slots_available === 1 ? '' : 's'
                  }}
                  available in Order #{{ order.number }} /
                </span>
                <ul role="presentation">
                  <li
                    v-for="index in (12-order.ppto_slots_available)"
                    :key="`filled${index}`"
                    class="filled"
                  ></li>
                  <li
                    v-for="index in (order.ppto_slots_available)"
                    :key="`unfilled${index}`"
                  ></li>
                </ul>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="columns text-center">
              <div class="customer-service">
                <p>
                  Have a question? Contact us at
                  <a :href="`tel:${customerServiceNumber}`" class="nowrap">{{
                    customerServiceNumberDisplay
                  }}</a>
                  {{ customerServiceHoursShort }}
                  <span class="nowrap">
                    or
                    <a :href="`mailto:${customerServiceEmail}`">
                      {{ customerServiceEmail }}
                    </a>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CountDown from '../../../common/components/CountDown.vue'
import ga4 from '~/../common/utils/ga4'

export default {
  name: 'CheckoutThankYouNew',
  components: {
    CountDown,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters([
      'settingsVars',
      'inMobileApp',
      'customerServiceNumber',
      'customerServiceNumberDisplay',
      'customerServiceEmail',
      'productNotifications',
      'customerServiceHoursShort',
    ]),
    allProductIds() {
      if (this.order.ppto_order) {
        return this.order.ppto_order.all_product_ids
      }
      return this.order.all_product_ids
    },
    orderLines() {
      if (this.order.ppto_order) {
        return this.order.ppto_order.lines
      }
      return this.order.lines
    },
    discountsTotal() {
      if (this.order.ppto_order) {
        return this.order.ppto_order.offer_discounts_total
      }
      return this.order.offer_discounts_total
    },
    taxTotal() {
      if (this.order.ppto_order) {
        return this.order.ppto_order.taxes
      }
      return this.order.child_tax_total
    },
    showPPTOWidget() {
      const currentDate = new Date()
      const shipDate = new Date(this.order.expected_ship_date)
      const dayDifference = (shipDate - currentDate) / 86400000
      const withinSevenDays = dayDifference >= 0 && dayDifference <= 7
      return (
        this.order.child_shipping_total === 0 &&
        this.order.ppto_slots_available > 0 &&
        !withinSevenDays
      )
    },
  },
  mounted() {
    this.$store.dispatch('setShowHeader', true)
    this.$store.dispatch('getPromoBanner')
    ga4.trackPurchase(this.order, this.$auth)
    if (this.order.show_add_club_get_10_pct_banner) {
      ga4.track('join_club_10_pct_seen')
    }
    this.track_affiliate_marketing()
    this.updateNotifications()
  },
  methods: {
    onClickOut(event) {
      ga4.track(event)
    },
    updateNotifications() {
      // Remove notifications for any wines that were successfully ordered
      this.productNotifications.forEach((x) => {
        const found = this.allProductIds.find(
          (pid) => pid === x.product.product_id
        )
        if (found) {
          this.$store.dispatch('removeNotification', x)
        }
      })
    },

    track_affiliate_marketing() {
      // This is used by Impact Affiliate tracking
      const customerStatus =
        this.order.user.new_or_returning_customer === 'New' ? 'New' : 'Existing'
      const productData = this.orderLines.map((line) => ({
        quantity: line.quantity,
        unitPrice: line.unit_price_incl_tax,
        unitPriceLessTax: line.unit_price_excl_tax,
        SKU: line.product.id,
        productName: line.description,
        optionalData: {
          cat: line.earmark_name,
        },
      }))
      /* eslint-disable */
      var rm_trans = {
        affiliateConfig: {
          ranMID: '42973',
          discountType: 'item',
          includeStatus: true,
        },
        orderid: this.order.number,
        currency: 'USD',
        customerStatus,
        conversionType: 'Sale',
        customerID: this.$auth.user.id,
        discountCode: '',
        discountAmount: this.discountsTotal,
        taxAmount: this.taxTotal,
        optionalData: {
          custstatus: customerStatus,
        },
        lineitems: productData,
      }
      if (!window.DataLayer) {
        window.DataLayer = { Sale: { Basket: rm_trans } }
      } else {
        DataLayer.Sale = DataLayer.Sale || { Basket: rm_trans }
        DataLayer.Sale.Basket = DataLayer.Sale.Basket || rm_trans
      }
      DataLayer.Sale.Basket.Ready = true
      function __readRMCookie(e) {
        for (
          var a = e + '=', t = document.cookie.split(';'), r = 0;
          r < t.length;
          r++
        ) {
          for (var i = t[r]; ' ' == i.charAt(0); ) i = i.substring(1, i.length)
          if (0 == i.indexOf(a)) return i.substring(a.length, i.length)
        }
        return ''
      }
      function __readRMCookiev2(e, a) {
        for (
          var t = __readRMCookie((a = a || 'rmStore'));
          t !== decodeURIComponent(t);

        )
          t = decodeURIComponent(t)
        for (var r = t.split('|'), i = 0; i < r.length; i++) {
          var n = r[i].split(':')[0],
            o = r[i].split(':')[1]
          if (n === e) return o
        }
        return ''
      }
      function __readParam(e, a, t, r) {
        var i = e || '',
          n = a || '',
          o = t || '',
          s = r || {},
          d = __readRMCookiev2(i),
          u = s[n],
          m = (d = s.ignoreCookie ? 0 : d) || u || o
        return (m = ('string' != typeof m || 'false' !== m.toLowerCase()) && m)
      }
      function sRAN() {
        var e =
            DataLayer && DataLayer.Sale && DataLayer.Sale.Basket
              ? DataLayer.Sale.Basket
              : {},
          a = e.affiliateConfig || {},
          t = __readParam('atm', 'tagType', 'pixel', a),
          r = __readParam('adr', 'discountType', 'order', a),
          i = __readParam('acs', 'includeStatus', 'false', a),
          n = __readParam('arto', 'removeOrderTax', 'false', a),
          o = __readParam('artp', 'removeTaxFromProducts', 'false', a),
          s = __readParam('artd', 'removeTaxFromDiscount', 'false', a),
          d = __readParam('atr', 'taxRate', 0, a)
        d = Number(d)
        var u =
            __readParam('ald', 'land', !1, {}) ||
            (a.land && !0 === a.land
              ? __readRMCookie('ranLandDateTime')
              : a.land) ||
            !1,
          m =
            __readParam('atrv', 'tr', !1, {}) ||
            (a.tr && !0 === a.tr ? __readRMCookie('ranSiteID') : a.tr) ||
            !1,
          l = !1,
          c = __readParam('amid', 'ranMID', '', a) || e.ranMID
        if (!c) return !1
        if (
          !(
            void 0 === a.allowCommission ||
            ('false' !== a.allowCommission && a.allowCommission)
          )
        )
          return !1
        var p = e.orderid || 'OrderNumberNotAvailable',
          f = '',
          y = '',
          v = '',
          _ = '',
          h = e.currency || '',
          g = e.taxAmount ? Math.abs(Math.round(100 * Number(e.taxAmount))) : 0,
          N = e.discountAmount
            ? Math.abs(Math.round(100 * Number(e.discountAmount)))
            : 0
        if (s && d)
          var C = (100 + Number(d)) / 100,
            N = Math.round(N / C)
        var w = 'pixel' === t ? 'ep' : 'mop' === t ? 'eventnvppixel' : 'ep',
          D = e.customerStatus || '',
          S = document.location.protocol + '//track.linksynergy.com/' + w + '?',
          b = ''
        null != D &&
          '' != D &&
          ((i && 'EXISTING' == D.toUpperCase()) ||
            (i && 'RETURNING' == D.toUpperCase())) &&
          (b = 'R_')
        for (
          var P = [], x = 0, k = 0;
          k < (e.lineitems ? e.lineitems.length : 0);
          k++
        ) {
          for (
            var T = !1,
              L = window.JSON
                ? JSON.parse(JSON.stringify(e.lineitems[k]))
                : e.lineitems[k],
              R = 0;
            R < P.length;
            R++
          )
            P[R].SKU === L.SKU &&
              ((T = !0),
              (P[R].quantity = Number(P[R].quantity) + Number(L.quantity)))
          T || P.push(L),
            (x +=
              Number(L.quantity) *
              Number(L.unitPriceLessTax || L.unitPrice) *
              100)
        }
        for (var I = {}, k = 0; k < P.length; k++) {
          var L = P[k],
            M = encodeURIComponent(L.SKU),
            A = L.unitPriceLessTax || L.unitPrice,
            O = L.quantity,
            U = encodeURIComponent(L.productName) || '',
            E = Math.round(Number(A) * Number(O) * 100)
          if (
            (!o ||
              !d ||
              (L.unitPriceLessTax && L.unitPriceLessTax !== L.unitPrice) ||
              (E /= C = (100 + d) / 100),
            'item' === r.toLowerCase() && N && (E -= (N * E) / x),
            L.optionalData)
          ) {
            q = L.optionalData
            for (var B in q)
              q.hasOwnProperty(B) && ((I[B] = I[B] || ''), (I[B] += q[B] + '|'))
          }
          ;(f += b + M + '|'),
            (y += O + '|'),
            (v += Math.round(E) + '|'),
            (_ += b + U + '|')
        }
        ;(f = f.slice(0, -1)),
          (y = y.slice(0, -1)),
          (v = v.slice(0, -1)),
          (_ = _.slice(0, -1)),
          N && 'order' === r.toLowerCase()
            ? ((f += '|' + b + 'DISCOUNT'),
              (_ += '|' + b + 'DISCOUNT'),
              (y += '|0'),
              (v += '|-' + N))
            : N && 'item' === r.toLowerCase() && (l = !0),
          n &&
            g &&
            ((f += '|' + b + 'ORDERTAX'),
            (y += '|0'),
            (v += '|-' + g),
            (_ += '|' + b + 'ORDERTAX')),
          (S +=
            'mid=' +
            c +
            '&ord=' +
            p +
            '&skulist=' +
            f +
            '&qlist=' +
            y +
            '&amtlist=' +
            v +
            '&cur=' +
            h +
            '&namelist=' +
            _ +
            '&img=1&'),
          u && (S += 'land=' + u + '&'),
          m && (S += 'tr=' + m + '&'),
          l && (S += 'discount=' + N + '&')
        try {
          S +=
            'caduid=' +
            (B = document.cookie.slice(
              document.cookie.match('stc[0-9]{6}=.*;').index
            ))
              .split(';')[0]
              .match('uid:[0-9.]*')[0]
              .split(':')[1] +
            '&'
        } catch (e) {}
        var q = e.optionalData || {}
        for (var B in q) q.hasOwnProperty(B) && (S += B + '=' + q[B] + '&')
        for (var B in I)
          I.hasOwnProperty(B) && (S += B + 'list=' + I[B].slice(0, -1) + '&')
        '&' === S[S.length - 1] && (S = S.slice(0, -1)),
          S.length >= 2048 && ((S = S.slice(0, 2037)), (S += '&trunc=true'))
        var j,
          G = document.createElement('img')
        G.setAttribute('src', S),
          G.setAttribute('height', '1px'),
          G.setAttribute('width', '1px'),
          (j = document.getElementsByTagName(
            'script'
          )[0]).parentNode.insertBefore(G, j)
      }
      function sDisplay() {
        var e = null,
          a = null,
          t = null,
          r = null,
          i =
            window.DataLayer &&
            window.DataLayer.Sale &&
            window.DataLayer.Sale.Basket
              ? window.DataLayer.Sale.Basket
              : {},
          n = i.displayConfig || {},
          o = i.customerStatus || '',
          s = i.discountAmount ? Math.abs(Number(i.discountAmount)) : 0,
          d = __readParam('dmid', 'rdMID', '', n)
        if (!d) return !1
        var u = __readParam('dtm', 'tagType', 'js', n),
          m =
            'if' === (u = 'js' === u || 'if' === u || 'img' === u ? u : 'js')
              ? 'iframe'
              : 'img' === u
              ? u
              : 'script',
          l =
            '//' +
            __readParam('ddn', 'domain', 'tags.rd.linksynergy.com', n) +
            '/' +
            u +
            '/' +
            d,
          c = __readParam('dis', 'includeStatus', 'false', n),
          p = ''
        if (
          (null != o &&
            '' != o &&
            ((c && 'EXISTING' == o.toUpperCase()) ||
              (c && 'RETURNING' == o.toUpperCase())) &&
            (p = 'R_'),
          !i.orderid || 'sale' !== i.conversionType.toLowerCase())
        )
          return !1
        ;(t = 0), (a = p + i.orderid), (e = ''), (r = 'conv')
        for (var f = 0; f < (i.lineitems ? i.lineitems.length : 0); f++)
          (t +=
            Number(i.lineitems[f].unitPrice) * Number(i.lineitems[f].quantity)),
            (e += i.lineitems[f].SKU + ',')
        ;(t = Math.round(100 * (t - s)) / 100),
          (e = e.slice(0, -1)) &&
            (l =
              l.indexOf('?') > -1 ? l + '&prodID=' + e : l + '/?prodID=' + e),
          a &&
            (l =
              l.indexOf('?') > -1
                ? l + '&orderNumber=' + a
                : l + '/?orderNumber=' + a),
          t &&
            (l = l.indexOf('?') > -1 ? l + '&price=' + t : l + '/?price=' + t),
          (l = l.indexOf('?') > -1 ? l + '&pt=' + r : l + '/?pt=' + r)
        var y = document.createElement(m)
        ;(y.src = l),
          'script' === m
            ? (y.type = 'text/javascript')
            : 'iframe' === m && y.setAttribute('style', 'display: none;'),
          document.getElementsByTagName('body')[0].appendChild(y)
      }
      function sSearch() {
        var e =
            window.DataLayer &&
            window.DataLayer.Sale &&
            window.DataLayer.Sale.Basket
              ? window.DataLayer.Sale.Basket
              : {},
          a = e.searchConfig || {},
          t = __readParam('smid', 'rsMID', '', a)
        if (!t) return !1
        var r = function () {
            var r = e.discountAmount ? Math.abs(Number(e.discountAmount)) : 0,
              i = __readParam('sct', 'conversionType', 'conv', a),
              n = 0,
              o = ''
            if (!e.orderid) return !1
            ;(n = 0), (o = e.orderid)
            for (var s = 0; s < (e.lineitems ? e.lineitems.length : 0); s++)
              n +=
                Number(e.lineitems[s].unitPrice) *
                Number(e.lineitems[s].quantity)
            ;(n = Math.round(100 * (n - r)) / 100), window.DataLayer.Sale.Basket
            var d = []
            ;(d[0] = 'id=' + t),
              (d[1] = 'type=' + i),
              (d[2] = 'val=' + n),
              (d[3] = 'orderId=' + o),
              (d[4] = 'promoCode=' + e.discountCode || ''),
              (d[5] = 'valueCurrency=' + e.currency || 'USD'),
              (d[6] = 'GCID='),
              (d[7] = 'kw='),
              (d[8] = 'product='),
              k_trackevent(d, '113')
          },
          i =
            document.location.protocol.indexOf('s') > -1
              ? 'https://'
              : 'http://'
        i += '113.xg4ken.com/media/getpx.php?cid=' + t
        var n = document.createElement('script')
        ;(n.type = 'text/javascript'),
          (n.src = i),
          (n.onload = r),
          (n.onreadystatechange = function () {
            ;('complete' != this.readyState && 'loaded' != this.readyState) ||
              r()
          }),
          document.getElementsByTagName('head')[0].appendChild(n)
      }
      sDisplay(), sSearch(), sRAN()
    },
  },
}
</script>
